import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import MasterLayout from "../masterLayout/MasterLayout";
import Breadcrumb from "../components/Breadcrumb";
import { Tab, Tabs, Form, Button, Row, Col, Card, Table, Modal } from "react-bootstrap";
import Select from "react-select";
import axios from "axios";
import { API_URL_LOCAL } from "..";
import { Icon } from "@iconify/react/dist/iconify.js";
import swal from "sweetalert";

const EmployeeDetailPage = () => {
  const { id } = useParams();
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editMode, setEditMode] = useState({});
  const [formData, setFormData] = useState({});
  const [locations, setLocations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [supervisors, setSupervisors] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [imagePreview, setImagePreview] = useState(null);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [documentData, setDocumentData] = useState({
    type: null,
    file: null,
    comments: "",
  });
  const [scheduleMode, setScheduleMode] = useState(null); // 'existing' or 'new'
  const [newScheduleData, setNewScheduleData] = useState({
    name: "",
    working_days: {
      Mon: false,
      Tue: false,
      Wed: false,
      Thu: false,
      Fri: false,
      Sat: false,
      Sun: false,
    },
    shift_start: "",
    shift_end: "",
    early_in: "",
    overtime: "",
    early_out: "",
    breaks: [{ minutes: "" }],
  });
  const [showLeaveQuotaModal, setShowLeaveQuotaModal] = useState(false);
  const [leaveQuotas, setLeaveQuotas] = useState([{ leave_type: null, days: "" }]);
  
  const leaveTypeOptions = [
    { value: "Annual", label: "Annual Leave" },
    { value: "Sick", label: "Sick Leave" },
    { value: "Maternity", label: "Maternity Leave" },
    { value: "Paternity", label: "Paternity Leave" },
    { value: "Casual", label: "Casual Leave" },
    { value: "Unpaid", label: "Unpaid Leave" },
  ];
  // Dropdown options
  const workModalOptions = [
    { value: "Remote", label: "Remote" },
    { value: "Onsite", label: "On-Site" },
  ];

  const employmentStatusOptions = [
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
    { value: "separated", label: "Separated" },
  ];

  const payrollTypeOptions = [
    { value: "Monthly", label: "Monthly" },
    { value: "Hourly", label: "Hourly" },
    { value: "Daily", label: "Daily" },
  ];

  const currencyOptions = [
    { value: "USD", label: "USD" },
    { value: "EUR", label: "EUR" },
    { value: "PKR", label: "PKR" },
  ];

  const separationTypes = [
    { value: "resignation", label: "Resignation" },
    { value: "termination", label: "Termination" },
    { value: "retirement", label: "Retirement" },
    { value: "layoff", label: "Layoff" },
  ];

  const separationReasons = [
    { value: "personal", label: "Personal Reasons" },
    { value: "performance", label: "Performance Issues" },
    { value: "relocation", label: "Relocation" },
    { value: "career_change", label: "Career Change" },
    { value: "other", label: "Other" },
  ];

  const documentTypeOptions = [
    { value: "General", label: "General" },
    { value: "Contract", label: "Contract" },
    { value: "Certificate", label: "Certificate" },
    { value: "ID", label: "ID" },
  ];
 
  // Fetch employee and related data
  const fetchData = async () => {
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      if (!user || !user.id) throw new Error("User not found in localStorage");

      // Fetch employee
      const empResponse = await axios.get(`${API_URL_LOCAL}/api/employee/fetch-single/${id}`);
      const empData = empResponse.data.employee;
      setEmployee(empData);
      setImagePreview(empData.profile_image);
      setFormData({
        personal: {
          first_name: empData.first_name || "",
          last_name: empData.last_name || "",
          email_address: empData.email_address || "",
          phone_number: empData.phone_number || "",
          username: empData.username || "",
          profile_image: null,
        },
        job: {
          location_id: null, // Will be set after locations are fetched
          department_id: null,
          supervisor_id: null,
          hiring_date: empData.job?.joing_date?.split("T")[0] || "",
          employment_status: employmentStatusOptions.find(
            (opt) => opt.value === empData.job?.employement_status
          ) || null,
          work_modal: workModalOptions.find(
            (opt) => opt.value === empData.job?.select_work_modal
          ) || null,
        },
        payroll: {
          payroll_type: payrollTypeOptions.find(
            (opt) => opt.value === empData.salary?.pay_frequency
          ) || null,
          currency: currencyOptions.find((opt) => opt.value === empData.salary?.currency_type) || null,
          amount: empData.salary?.salary_amount?.toString() || "",
          overtime_rate: empData.salary?.overtime_rate?.toString() || "",
          pay_grade: empData.salary?.pay_grade || "",
        },
        password: {
          current_password: "",
          new_password: "",
          current_pin: "",
          new_pin: "",
        },
        separation: {
          type: separationTypes.find((t) => t.value === empData.job?.terminate_status) || null,
          reason: separationReasons.find((r) => r.value === empData.job?.terminate_message) || null,
          reason_comments: empData.job?.reason_comments || "",
          date: empData.job?.terminate_date?.split("T")[0] || "",
          comments: empData.job?.comments || "",
          clearance_date: empData.job?.clearance_date?.split("T")[0] || "",
        },
        privileges: {
          add_employee: empData.employee_right || false,
          mark_attendance: empData.mark_right || false,
          enable_gps: empData.tracking_status || false,
        },
        schedule: {
            schedule_id: null, // Will be set after schedules are fetched
            data: empData.schedule_type === 'custom' ? empData.custom_schedule : empData.general_schedule
          },
      });
    //   console.log(empData)
    let initialLeaveQuotas = [{ leave_type: null, days: "" }];
    if (
      empData.job?.annual_leaves &&
      empData.job?.annual_leaves_type
    ) {
      const leaveCounter = empData.job.annual_leaves.split(",");
      const leaveType = empData.job.annual_leaves_type.split(",");
      if (leaveCounter.length === leaveType.length) {
        initialLeaveQuotas = leaveCounter.map((days, index) => {
          const type = leaveType[index];
          const option = leaveTypeOptions.find((opt) => opt.value === type);
          return {
            leave_type: option || { value: type, label: type }, // Fallback if type not in options
            days: days.trim(),
          };
        });
      }
    }
    setLeaveQuotas(initialLeaveQuotas);

      // Fetch locations
      const locResponse = await axios.get(`${API_URL_LOCAL}/api/company/fetch/${user.id}`);
      const locData = locResponse.data.companies.map((loc) => ({
        value: loc.id,
        label: loc.location,
      }));
      setLocations(locData);

      // Update formData with location after fetching
      setFormData((prev) => ({
        ...prev,
        job: {
          ...prev.job,
          location_id: locData.find((loc) => loc.value === empData.company_id) || null,
        },
      }));

      // Fetch departments and supervisors
      const depResponse = await axios.get(
        `${API_URL_LOCAL}/api/departments/fetch-by-location/${empData.company_id}`
      );
      const depData = depResponse.data.departments.map((dep) => ({
        value: dep.id,
        label: dep.name,
        location_id: dep.location,
      }));
      setDepartments(depData);

      const supData = depResponse.data.supervisor?.map((sup) => ({
        value: sup.id,
        label: `${sup.first_name} ${sup.last_name}`,
        location_id: sup.company_id,
      })) || [];
      setSupervisors(supData);

      // Update formData with department and supervisor
      setFormData((prev) => ({
        ...prev,
        job: {
          ...prev.job,
          department_id: depData.find((dep) => dep.value === empData.job?.department?.id) || null,
          supervisor_id: supData.find((sup) => sup.value === empData.super_id) || null,
        },
      }));

      // Fetch schedules

      const schedResponse = await axios.get(`${API_URL_LOCAL}/api/work-schedule/org/${user.id}`);
      
      const schedData = schedResponse.data?.map((s) => ({
        value: s.id,
        label: s.schedule_name,
      }));
      setSchedules(schedData);

      // Update formData with schedule
      setFormData((prev) => ({
        ...prev,
        schedule: {
          schedule_id: schedData.find((s) => s.value === empData.schedule_id) || null,
        },
      }));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchData();
  }, [id]);

  // Update departments and supervisors when location changes
  useEffect(() => {
    if (editMode.job && formData.job.location_id) {
      const fetchLocationData = async () => {
        try {
          const depResponse = await axios.get(
            `${API_URL_LOCAL}/api/departments/fetch-by-location/${formData.job.location_id.value}`
          );
          setDepartments(
            depResponse.data.departments.map((dep) => ({
              value: dep.id,
              label: dep.name,
              location_id: dep.location,
            })) || []
          );
          setSupervisors(
            depResponse.data.supervisor?.map((sup) => ({
              value: sup.id,
              label: `${sup.first_name} ${sup.last_name}`,
              location_id: sup.company_id,
            })) || []
          );
          setFormData((prev) => ({
            ...prev,
            job: {
              ...prev.job,
              department_id: null,
              supervisor_id: null,
            },
          }));
        } catch (err) {
          console.error("Failed to fetch location data:", err);
        }
      };
      fetchLocationData();
    }
  }, [formData.job?.location_id]);

  // Handle Edit/Save toggle
  const toggleEdit = (tab) => {
    setEditMode({ ...editMode, [tab]: !editMode[tab] });
    if (tab === "schedule" && !editMode[tab]) {
        setScheduleMode(null);
    }
  };

  // Handle form changes
  const handleChange = (tab, field, value) => {
    setFormData({
      ...formData,
      [tab]: { ...formData[tab], [field]: value },
    });
  };

  const handleSelectChange = (tab, field, selected) => {
    setFormData({
      ...formData,
      [tab]: { ...formData[tab], [field]: selected },
    });
  };

  const handleCheckboxChange = (tab, field) => {
    setFormData({
      ...formData,
      [tab]: { ...formData[tab], [field]: !formData[tab][field] },
    });
  };

  // Handle image upload
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({
        ...formData,
        personal: { ...formData.personal, profile_image: file },
      });
      setImagePreview(URL.createObjectURL(file));
    }
  };

  // Handle document modal changes
  const handleDocumentChange = (field, value) => {
    setDocumentData({ ...documentData, [field]: value });
  };

  // Handle new schedule form changes
  const handleNewScheduleChange = (field, value) => {
    setNewScheduleData({ ...newScheduleData, [field]: value });
  };

  const handleWorkingDayChange = (day) => {
    setNewScheduleData({
      ...newScheduleData,
      working_days: {
        ...newScheduleData.working_days,
        [day]: !newScheduleData.working_days[day],
      },
    });
  };

  const handleBreakChange = (index, value) => {
    const updatedBreaks = [...newScheduleData.breaks];
    updatedBreaks[index] = { minutes: value };
    setNewScheduleData({ ...newScheduleData, breaks: updatedBreaks });
  };

  const addBreak = () => {
    setNewScheduleData({
      ...newScheduleData,
      breaks: [...newScheduleData.breaks, { minutes: "" }],
    });
  };

  const removeBreak = (index) => {
    setNewScheduleData({
      ...newScheduleData,
      breaks: newScheduleData.breaks.filter((_, i) => i !== index),
    });
  };

  // Save document
  const handleSaveDocument = async () => {
    try {
      if (!documentData.type || !documentData.file) {
        alert("Document Type and File are required");
        return;
      }

      const formDataPayload = new FormData();
      formDataPayload.append("emp_id", id);
      formDataPayload.append("type", documentData.type.value);
      formDataPayload.append("file", documentData.file);
      formDataPayload.append("comments", documentData.comments);

      const response = await axios.post(
        `${API_URL_LOCAL}/api/employee/documents/upload`,
        formDataPayload,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      if (response.status === 201) {
        setEmployee({
          ...employee,
          EmpQualificationAttachments: [
            ...(employee.EmpQualificationAttachments || []),
            response.data.document,
          ],
        });
        setShowDocumentModal(false);
        setDocumentData({ type: null, file: null, comments: "" });
        swal("Document Upload","Document uploaded successfully!" , "success");
      }
    } catch (err) {
      alert("Failed to upload document: " + err.message);
    }
  };

  // Save changes
  const handleSave = async (tab, subTab = null) => {
    try {
      let payload = {};
      if (tab === "personal") {
        if (!formData.personal.first_name || !formData.personal.last_name) {
          alert("First Name and Last Name are required");
          return;
        }
        payload = { ...formData.personal };
        if (formData.personal.profile_image) {
          const imageFormData = new FormData();
          imageFormData.append("profile_image", formData.personal.profile_image);
          const imageResponse = await axios.post(
            `${API_URL_LOCAL}/api/employee/${id}/upload-image`,
            imageFormData,
            { headers: { "Content-Type": "multipart/form-data" } }
          );
          payload.profile_image = imageResponse.data.url;
        }
      } else if (tab === "job") {
        if (!formData.job.location_id || !formData.job.department_id || !formData.job.hiring_date) {
          alert("Location, Department, and Hiring Date are required");
          return;
        }
        payload = {
          company_id: formData.job.location_id?.value || null,
          department_id: formData.job.department_id?.value || null,
          super_id: formData.job.supervisor_id?.value || null,
          joing_date: formData.job.hiring_date,
          employement_status: formData.job.employment_status?.value || null,
          select_work_modal: formData.job.work_modal?.value || null,
        };
      } else if (tab === "payroll") {
        if (!formData.payroll.payroll_type || !formData.payroll.currency || !formData.payroll.amount) {
          alert("Payroll Type, Currency, and Amount are required");
          return;
        }
        payload = {
          pay_frequency: formData.payroll.payroll_type?.value || null,
          currency_type: formData.payroll.currency?.value || null,
          salary_amount: parseFloat(formData.payroll.amount),
          overtime_rate: parseFloat(formData.payroll.overtime_rate || 0),
          pay_grade: formData.payroll.pay_grade,
        };
      } else if (tab === "password") {
        if (subTab === "password") {
          if (!formData.password.current_password || !formData.password.new_password) {
            alert("Please provide both current and new password");
            return;
          }
          payload = {
            current_password: formData.password.current_password,
            new_password: formData.password.new_password,
            subTab,
          };
        } else if (subTab === "pin") {
          if (!formData.password.current_pin || !formData.password.new_pin) {
            alert("Please provide both current and new PIN");
            return;
          }
          payload = {
            currentpin: formData.password.current_pin,
            newpin: formData.password.new_pin,
            subTab,
          };
        }
      } else if (tab === "separation") {
        if (
          !formData.separation.type ||
          !formData.separation.reason ||
          !formData.separation.date ||
          !formData.separation.clearance_date
        ) {
          alert("Separation Type, Reason, Date, and Clearance Date are required");
          return;
        }
        payload = {
          terminate_status: formData.separation.type?.value || null,
          terminate_message: formData.separation.reason?.value || null,
          reason_comments: formData.separation.reason_comments,
          terminate_date: formData.separation.date,
          comments: formData.separation.comments,
          clearance_date: formData.separation.clearance_date,
        };
      } else if (tab === "privileges") {
        payload = {
          employee_right: formData.privileges.add_employee,
          mark_right: formData.privileges.mark_attendance,
          tracking_status: formData.privileges.enable_gps,
        };
      } else if (tab === "schedule") {
        if (scheduleMode === "existing") {
          if (!formData.schedule.schedule_id) {
            alert("Please select a schedule");
            return;
          }
          payload = {
            schedule_id: formData.schedule.schedule_id.value,
            type: 'general'
          };
        } else if (scheduleMode === "new") {
          if (
           
            !Object.values(newScheduleData.working_days).some((day) => day) ||
            !newScheduleData.shift_start ||
            !newScheduleData.shift_end
          ) {
            alert("Schedule Name, at least one Working Day, Shift Start, and Shift End are required");
            return;
          }
          if (newScheduleData.breaks.some((b) => !b.minutes)) {
            alert("All breaks must have a valid number of minutes");
            return;
          }
          const workingDaysArray = Object.keys(newScheduleData.working_days).filter(
            (day) => newScheduleData.working_days[day]
          );
          const user = JSON.parse(localStorage.getItem("user"));
          const schedulePayload = {
            name: newScheduleData.name,
            work_days: workingDaysArray.toString(),
            shift_start: newScheduleData.shift_start,
            shift_end: newScheduleData.shift_end,
            early_in: newScheduleData.early_in ? parseInt(newScheduleData.early_in) : 0,
            overtime: newScheduleData.overtime ? parseInt(newScheduleData.overtime) : 0,
            early_out: newScheduleData.early_out ? parseInt(newScheduleData.early_out) : 0,
            no_of_breaks: newScheduleData.breaks.length,
            break_mints: newScheduleData.breaks.map((e)=>e.minutes).toString(),
            org_id : user.id,
            type:'new'
            
          };
          
          const createResponse = await axios.post(
            `${API_URL_LOCAL}/api/employee/update-single-schedule/${id}`,
             schedulePayload,
            {
              headers: { "Content-Type": "application/json" },
            }
          );
          if (createResponse.status === 201) {
            const newSchedule = createResponse.data.schedule;
            setSchedules([...schedules, { value: newSchedule.id, label: newSchedule.name }]);
            payload = { schedule_id: newSchedule.id , type: 'custom' };
          } else {
            throw new Error("Failed to create schedule");
          }
        }
      }
      
      const response = await axios.put(
        `${API_URL_LOCAL}/api/employee/update-single/${id}/${tab}`,
        payload,
        {
          headers: { "Content-Type": "application/json" },
        }
      );

      if (response.status === 200) {
        setEmployee({
          ...employee,
          ...payload,
          job: tab === "job" ? { ...employee.job, ...payload } : employee.job,
          salary: tab === "payroll" ? { ...employee.salary, ...payload } : employee.salary,
          profile_image: payload.profile_image || employee.profile_image,
          schedule_id: tab === "schedule" ? payload.schedule_id : employee.schedule_id,
        });
        setEditMode({ ...editMode, [tab]: false });
        setScheduleMode(null);
        setNewScheduleData({
          name: "",
          working_days: {
            Monday: false,
            Tuesday: false,
            Wednesday: false,
            Thursday: false,
            Friday: false,
            Saturday: false,
            Sunday: false,
          },
          shift_start: "",
          shift_end: "",
          early_in: "",
          overtime: "",
          early_out: "",
          breaks: [{ minutes: "" }],
        });
        swal("Information Updated" , "Changes saved successfully!" , "success") ;
      }

    } catch (err) {
        
        if(err.status== 409){
            swal("Username Validation","Username is already taken. Please change it." , "error");
            return false;
          }
         alert("Failed to save changes: " + err.message);
    }
  };
  const handleDocumentDelete = (documentId) => {
    swal({
      title: "Are you sure?",
      text: "You want to delete this document?",
      icon: "warning",
      buttons: ["No", "Yes"],
      dangerMode: true,
    }).then(async (willDelete) => {
      if (willDelete) {
        try {
          const response = await axios.delete(
            `${API_URL_LOCAL}/api/employee/documents/${documentId}`
          );
          if (response.status === 200) {
            // Remove the document from employee state
            setEmployee({
              ...employee,
              EmpQualificationAttachments: employee.EmpQualificationAttachments.filter(
                (doc) => doc.id !== documentId
              ),
            });
            swal("Success", "Document deleted successfully!", "success");
          } else {
            throw new Error("Failed to delete document");
          }
        } catch (err) {
          swal("Error", `Failed to delete document: ${err.message}`, "error");
        }
      }
      // No action needed if "No" is clicked; alert closes automatically
    });
  };
  const handleLeaveQuotaChange = (index, field, value) => {
    const updatedQuotas = [...leaveQuotas];
    updatedQuotas[index] = { ...updatedQuotas[index], [field]: value };
    setLeaveQuotas(updatedQuotas);
  };
  
  const addLeaveQuota = () => {
    setLeaveQuotas([...leaveQuotas, { leave_type: null, days: "" }]);
  };
  
  const removeLeaveQuota = (index) => {
    setLeaveQuotas(leaveQuotas.filter((_, i) => i !== index));
  };
  
  const handleSaveLeaveQuotas = async () => {
    try {
      // Validate quotas
      for (const quota of leaveQuotas) {
        if (!quota.leave_type || !quota.days || quota.days <= 0) {
          swal("Validation Error","Please select a leave type and enter a valid number of days for all quotas" , "error");
          return;
        }
      }
      const payload = {
        employee_id: id,
        no_of_leaves: leaveQuotas.map((quota) =>  parseInt(quota.days)).toString(),
        leave_type: leaveQuotas.map((quota) =>  quota.leave_type.value).toString(),
      };
  
      const response = await axios.post(`${API_URL_LOCAL}/api/employee/leave-quota`, payload, {
        headers: { "Content-Type": "application/json" },
      });
  
      if (response.status === 201) {
        let initialLeaveQuotas = [{ leave_type: null, days: "" }];
      if (
        response.data.leave_quotas?.annual_leaves &&
        response.data.leave_quotas?.annual_leaves_type
      ) {
        const leaveCounter = response.data.leave_quotas.annual_leaves.split(",");
        const leaveType = response.data.leave_quotas.annual_leaves_type.split(",");
        if (leaveCounter.length === leaveType.length) {
          initialLeaveQuotas = leaveCounter.map((days, index) => {
            const type = leaveType[index];
            const option = leaveTypeOptions.find((opt) => opt.value === type);
            return {
              leave_type: option || { value: type, label: type }, // Fallback if type not in options
              days: days.trim(),
            };
          });
        }
      }
      setLeaveQuotas(initialLeaveQuotas);
        setEmployee({
          ...employee,
          job:response.data.leave_quotas,
        });
        setShowLeaveQuotaModal(false);
        // setLeaveQuotas([{ leave_type: null, days: "" }]);
        swal("Quota Assigned","Leave quotas assigned successfully!" , "success");
      } else {
        throw new Error("Failed to assign leave quotas");
      }
    } catch (err) {
      alert("Failed to assign leave quotas: " + err.message);
    }
  };
  if (loading) return <div className="text-center">Loading...</div>;
  if (error)
    return (
      <div className="alert alert-danger bg-danger-50 text-danger-600 border-danger-50 px-24 py-11 mb-20 fw-semibold text-lg radius-8">
        {error}
      </div>
    );

  // Filter departments and supervisors by location
  const filteredDepartments = formData.job?.location_id
    ? departments.filter((dep) => dep.location_id === formData.job.location_id.value)
    : departments;
  const filteredSupervisors = formData.job?.location_id
    ? supervisors.filter((sup) => sup.location_id === formData.job.location_id.value)
    : supervisors;

  return (
    <MasterLayout>
      <Breadcrumb title="Employee Details" />
      <div className="card h-100 p-0 radius-12">
        <div className="card-body p-24">
          {/* Profile Section */}
          <div className="d-flex align-items-center mb-24">
            <img
              src={imagePreview || "/default-avatar.png"}
              alt="Profile"
              style={{ width: "80px", height: "80px", borderRadius: "50%", marginRight: "16px" }}
            />
            <div>
              <h4 className="fw-semibold text-primary-dark">
                {employee.first_name} {employee.last_name}
              </h4>
              <p className="text-secondary-light">{employee.job?.job_title || "N/A"}</p>
            </div>
          </div>

          {/* Tabs */}
          <Tabs defaultActiveKey="personal" id="employee-tabs" className="mb-24">
            {/* Personal Tab */}
            <Tab eventKey="personal" title="Personal">
              <Card className="border">
                <Card.Body>
                  <Form>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-20">
                          <Form.Label className="fw-semibold text-primary-light text-sm mb-8">
                            <Icon fontSize={22} icon="mdi:account" className="me-2" />
                            First Name
                          </Form.Label>
                          {editMode.personal ? (
                            <Form.Control
                              type="text"
                              value={formData.personal.first_name}
                              onChange={(e) => handleChange("personal", "first_name", e.target.value)}
                              className="radius-8"
                            />
                          ) : (
                            <p>{employee.first_name || "N/A"}</p>
                          )}
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-20">
                          <Form.Label className="fw-semibold text-primary-light text-sm mb-8">
                            <Icon fontSize={22} icon="mdi:account-outline" className="me-2" />
                            Last Name
                          </Form.Label>
                          {editMode.personal ? (
                            <Form.Control
                              type="text"
                              value={formData.personal.last_name}
                              onChange={(e) => handleChange("personal", "last_name", e.target.value)}
                              className="radius-8"
                            />
                          ) : (
                            <p>{employee.last_name || "N/A"}</p>
                          )}
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-20">
                          <Form.Label className="fw-semibold text-primary-light text-sm mb-8">
                            <Icon fontSize={22} icon="mdi:email" className="me-2" />
                            Email Address
                          </Form.Label>
                          {editMode.personal ? (
                            <Form.Control
                              type="email"
                              value={formData.personal.email_address}
                              onChange={(e) =>
                                handleChange("personal", "email_address", e.target.value)
                              }
                              className="radius-8"
                            />
                          ) : (
                            <p>{employee.email_address || "N/A"}</p>
                          )}
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-20">
                          <Form.Label className="fw-semibold text-primary-light text-sm mb-8">
                            <Icon fontSize={22} icon="mdi:phone" className="me-2" />
                            Phone Number
                          </Form.Label>
                          {editMode.personal ? (
                            <Form.Control
                              type="text"
                              value={formData.personal.phone_number}
                              onChange={(e) =>
                                handleChange("personal", "phone_number", e.target.value)
                              }
                              className="radius-8"
                            />
                          ) : (
                            <p>{employee.phone_number || "N/A"}</p>
                          )}
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-20">
                          <Form.Label className="fw-semibold text-primary-light text-sm mb-8">
                            <Icon fontSize={22} icon="mdi:account-circle" className="me-2" />
                            Username
                          </Form.Label>
                          {editMode.personal ? (
                            <Form.Control
                              type="text"
                              value={formData.personal.username}
                              onChange={(e) => handleChange("personal", "username", e.target.value)}
                              className="radius-8"
                            />
                          ) : (
                            <p>{employee.username || "N/A"}</p>
                          )}
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-20">
                          <Form.Label className="fw-semibold text-primary-light text-sm mb-8">
                            <Icon fontSize={22} icon="mdi:image" className="me-2" />
                            Profile Image
                          </Form.Label>
                          {editMode.personal ? (
                            <Form.Control
                              type="file"
                              accept="image/*"
                              onChange={handleImageChange}
                              className="radius-8"
                            />
                          ) : (
                            <p>{employee.profile_image ? "Image Set" : "N/A"}</p>
                          )}
                        </Form.Group>
                      </Col>
                    </Row>
                    <div className="d-flex justify-content-end">
                      <Button
                        variant={editMode.personal ? "primary" : "outline-primary"}
                        className="text-md px-24 py-8 radius-8"
                        onClick={() =>
                          editMode.personal ? handleSave("personal") : toggleEdit("personal")
                        }
                      >
                        <Icon
                          fontSize={22}
                          icon={editMode.personal ? "mdi:content-save" : "mdi:pencil"}
                          className="me-2"
                        />
                        {editMode.personal ? "Save" : "Edit"}
                      </Button>
                    </div>
                  </Form>
                </Card.Body>
              </Card>
            </Tab>

            {/* Job Tab */}
            <Tab eventKey="job" title="Job">
              <Card className="border">
                <Card.Body>
                  <Form>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-20">
                          <Form.Label className="fw-semibold text-primary-light text-sm mb-8">
                            <Icon fontSize={22} icon="mdi:map-marker" className="me-2" />
                            Location
                          </Form.Label>
                          {editMode.job ? (
                            <Select
                              options={locations}
                              value={formData.job.location_id}
                              onChange={(selected) => handleSelectChange("job", "location_id", selected)}
                              placeholder="Select Location"
                            />
                          ) : (
                            <p>{employee.company?.location || "N/A"}</p>
                          )}
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-20">
                          <Form.Label className="fw-semibold text-primary-light text-sm mb-8">
                            <Icon fontSize={22} icon="mdi:briefcase" className="me-2" />
                            Department
                          </Form.Label>
                          {editMode.job ? (
                            <Select
                              options={filteredDepartments}
                              value={formData.job.department_id}
                              onChange={(selected) =>
                                handleSelectChange("job", "department_id", selected)
                              }
                              placeholder="Select Department"
                              isDisabled={!formData.job.location_id}
                            />
                          ) : (
                            <p>{employee.job?.department?.name || "N/A"}</p>
                          )}
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-20">
                          <Form.Label className="fw-semibold text-primary-light text-sm mb-8">
                            <Icon fontSize={22} icon="mdi:account-supervisor" className="me-2" />
                            Supervisor
                          </Form.Label>
                          {editMode.job ? (
                            <Select
                              options={filteredSupervisors}
                              value={formData.job.supervisor_id}
                              onChange={(selected) =>
                                handleSelectChange("job", "supervisor_id", selected)
                              }
                              placeholder="Select Supervisor"
                              isDisabled={!formData.job.location_id}
                            />
                          ) : (
                            <p>
                              {supervisors.find((sup) => sup.value === employee.super_id)?.label ||
                                "N/A"}
                            </p>
                          )}
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-20">
                          <Form.Label className="fw-semibold text-primary-light text-sm mb-8">
                            <Icon fontSize={22} icon="mdi:calendar" className="me-2" />
                            Hiring Date
                          </Form.Label>
                          {editMode.job ? (
                            <Form.Control
                              type="date"
                              value={formData.job.hiring_date}
                              onChange={(e) => handleChange("job", "hiring_date", e.target.value)}
                              className="radius-8"
                            />
                          ) : (
                            <p>
                              {employee.job?.joing_date
                                ? new Date(employee.job.joing_date).toLocaleDateString()
                                : "N/A"}
                            </p>
                          )}
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-20">
                          <Form.Label className="fw-semibold text-primary-light text-sm mb-8">
                            <Icon fontSize={22} icon="mdi:account-check" className="me-2" />
                            Employment Status
                          </Form.Label>
                          {editMode.job ? (
                            <Select
                              options={employmentStatusOptions}
                              value={formData.job.employment_status}
                              onChange={(selected) =>
                                handleSelectChange("job", "employment_status", selected)
                              }
                              placeholder="Select Status"
                            />
                          ) : (
                            <p>{employee.job?.employement_status || "N/A"}</p>
                          )}
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-20">
                          <Form.Label className="fw-semibold text-primary-light text-sm mb-8">
                            <Icon fontSize={22} icon="mdi:monitor" className="me-2" />
                            Work Modal
                          </Form.Label>
                          {editMode.job ? (
                            <Select
                              options={workModalOptions}
                              value={formData.job.work_modal}
                              onChange={(selected) => handleSelectChange("job", "work_modal", selected)}
                              placeholder="Select Work Modal"
                            />
                          ) : (
                            <p>{employee.job?.select_work_modal || "N/A"}</p>
                          )}
                        </Form.Group>
                      </Col>
                    </Row>
                    <div className="d-flex justify-content-end">
                      <Button
                        variant={editMode.job ? "primary" : "outline-primary"}
                        className="text-md px-24 py-8 radius-8"
                        onClick={() => (editMode.job ? handleSave("job") : toggleEdit("job"))}
                      >
                        <Icon
                          fontSize={22}
                          icon={editMode.job ? "mdi:content-save" : "mdi:pencil"}
                          className="me-2"
                        />
                        {editMode.job ? "Save" : "Edit"}
                      </Button>
                    </div>
                  </Form>
                </Card.Body>
              </Card>
            </Tab>

            {/* Documents Tab */}
            <Tab eventKey="documents" title="Documents">
              <Card className="border">
                <Card.Body>
                  <div className="d-flex justify-content-between mb-20">
                    <h6>Documents</h6>
                    <Button
                      variant="primary"
                      className="text-md px-24 py-8 radius-8"
                      onClick={() => setShowDocumentModal(true)}
                    >
                      <Icon fontSize={22} icon="mdi:plus" className="me-2" />
                      Add Document
                    </Button>
                  </div>
                  <Table striped bordered hover>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Type</th>
                        <th>Size</th>
                        <th>Comments</th>
                        <th>URL</th>
                      </tr>
                    </thead>
                    <tbody>
                      {employee.EmpQualificationAttachments?.length > 0 ? (
                        employee.EmpQualificationAttachments.map((doc) => (
                          <tr key={doc.id}>
                            <td>{doc.name}</td>
                            <td>{doc.type}</td>
                            <td>{(doc.size / 1024).toFixed(2)} KB</td>
                            <td>{doc.comments || "N/A"}</td>
                            <td>
                              <a href={doc.url} target="_blank" rel="noopener noreferrer">
                                View
                              </a>
                              <button type="button" className="btn" onClick={()=>handleDocumentDelete(doc.id)} >
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="5">No documents found</td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Tab>

            {/* Schedule Tab */}
            <Tab eventKey="schedule" title="Schedule">
              <Card className="border">
                <Card.Body>
                  <Form>
                    {!editMode.schedule ? (
                      <>
                        <Form.Group className="mb-20">
                          <Form.Label className="fw-semibold text-primary-light text-sm mb-8">
                            <Icon fontSize={22} icon="mdi:calendar-clock" className="me-2" />
                            Assigned Schedule
                          </Form.Label>
                          <p>
                            {schedules.find((s) => s.value === employee.schedule_id)?.label || employee?.schedule_type}
                          </p>
                        </Form.Group>
                        <Form.Group className="mb-20">
                                        <Form.Label className="fw-semibold text-primary-light text-sm mb-8">
                                            <Icon fontSize={22} icon="mdi:clock-outline" className="me-2" />
                                            Schedule Type
                                        </Form.Label>
                                        <p>{employee.schedule_type || "N/A"}</p>
                                        </Form.Group>
                        {
                            employee.schedule_type == 'general' ? 
                                <Row>
                                    <Col md="12">
                                       
                                        <Form.Group>
                                        <Form.Label className="fw-semibold text-primary-light text-sm mb-8">
                                            <Icon fontSize={22} icon="mdi:clock-outline" className="me-2" />
                                            Working Days ({employee?.general_schedule?.work_days.split(',').length})
                                        </Form.Label>
                                        <p>{employee.general_schedule?.work_days || "N/A"}</p>
                                        </Form.Group>
                                    </Col>
                                    <Col md="12">
                                        <Row>
                                        <Col md="4">
                                            <Form.Group>
                                            <Form.Label className="fw-semibold text-primary-light text-sm mb-8">
                                                <Icon fontSize={22} icon="mdi:clock-outline" className="me-2" />
                                                Shift Start 
                                            </Form.Label>
                                              <p>{employee.general_schedule?.start_time || "N/A"}</p>
                                            </Form.Group>
                                    </Col>
                                    <Col md="4">
                                            <Form.Group>
                                            <Form.Label className="fw-semibold text-primary-light text-sm mb-8">
                                                <Icon fontSize={22} icon="mdi:clock-outline" className="me-2" />
                                                Shift End 
                                            </Form.Label>
                                              <p>{employee.general_schedule?.end_time || "N/A"}</p>
                                            </Form.Group>
                                    </Col>
                                        </Row>
                                    </Col>
                                    <Col md="4">
                                            <Form.Group>
                                            <Form.Label className="fw-semibold text-primary-light text-sm mb-8">
                                                <Icon fontSize={22} icon="mdi:clock-outline" className="me-2" />
                                            Early In
                                            </Form.Label>
                                              <p>{employee.general_schedule?.trady_margin || "N/A"}</p>
                                            </Form.Group>
                                    </Col>
                                    <Col md="4">
                                            <Form.Group>
                                            <Form.Label className="fw-semibold text-primary-light text-sm mb-8">
                                                <Icon fontSize={22} icon="mdi:clock-outline" className="me-2" />
                                                Early Out
                                            </Form.Label>
                                            <p>{employee.general_schedule?.early_out_margin || "N/A"}</p>
                                            </Form.Group>
                                    </Col>
                                      <Col md="4">
                                            <Form.Group>
                                            <Form.Label className="fw-semibold text-primary-light text-sm mb-8">
                                                <Icon fontSize={22} icon="mdi:clock-outline" className="me-2" />
                                                Early Out
                                            </Form.Label>
                                            <p>{employee.general_schedule?.overtime_margin || "N/A"}</p>
                                            </Form.Group>
                                    </Col>
                                       <Col md="4">
                                            <Form.Group>
                                            <Form.Label className="fw-semibold text-primary-light text-sm mb-8">
                                                <Icon fontSize={22} icon="mdi:clock-outline" className="me-2" />
                                                No Of Breaks
                                            </Form.Label>
                                            <p>{employee.general_schedule?.no_of_breaks || "N/A"}</p>
                                            </Form.Group>
                                      </Col>
                                      <Col md="12">
                                        <Row>
                                        <Col md="6">
                                            <Form.Group>
                                            <Form.Label className="fw-semibold text-primary-light text-sm mb-8">
                                                <Icon fontSize={22} icon="mdi:clock-outline" className="me-2" />
                                               Breaks Mints
                                            </Form.Label>
                                            <table className="table table-striped">
                                                <thead>
                                                    <th>Sr No</th>
                                                    <th>Mints</th>
                                                </thead>
                                            <tbody>
                                            {employee.general_schedule?.breaks_mints.split(',').map((e,index)=><tr >
                                                <td>{index+1}</td>
                                                <td>{e} mints</td></tr>) || "N/A"}

                                            </tbody>
                                            </table>
                                            
                                            </Form.Group>
                                      </Col>
                                        </Row>
                                      </Col>
                                </Row> : null 
                            
                        }
                        {
                            employee.schedule_type == 'custom' ? 
                                <Row>
                                    <Col md="12">
                                       
                                        <Form.Group>
                                        <Form.Label className="fw-semibold text-primary-light text-sm mb-8">
                                            <Icon fontSize={22} icon="mdi:clock-outline" className="me-2" />
                                            Working Days ({employee?.custom_schedule?.work_days.split(',').length})
                                        </Form.Label>
                                        <p>{employee.custom_schedule.work_days || "N/A"}</p>
                                        </Form.Group>
                                    </Col>
                                    <Col md="12">
                                        <Row>
                                        <Col md="4">
                                            <Form.Group>
                                            <Form.Label className="fw-semibold text-primary-light text-sm mb-8">
                                                <Icon fontSize={22} icon="mdi:clock-outline" className="me-2" />
                                                Shift Start 
                                            </Form.Label>
                                              <p>{employee.custom_schedule.start_time || "N/A"}</p>
                                            </Form.Group>
                                    </Col>
                                    <Col md="4">
                                            <Form.Group>
                                            <Form.Label className="fw-semibold text-primary-light text-sm mb-8">
                                                <Icon fontSize={22} icon="mdi:clock-outline" className="me-2" />
                                                Shift End 
                                            </Form.Label>
                                              <p>{employee.custom_schedule.end_time || "N/A"}</p>
                                            </Form.Group>
                                    </Col>
                                        </Row>
                                    </Col>
                                    <Col md="4">
                                            <Form.Group>
                                            <Form.Label className="fw-semibold text-primary-light text-sm mb-8">
                                                <Icon fontSize={22} icon="mdi:clock-outline" className="me-2" />
                                            Early In
                                            </Form.Label>
                                              <p>{employee.custom_schedule.trady_margin || "N/A"}</p>
                                            </Form.Group>
                                    </Col>
                                    <Col md="4">
                                            <Form.Group>
                                            <Form.Label className="fw-semibold text-primary-light text-sm mb-8">
                                                <Icon fontSize={22} icon="mdi:clock-outline" className="me-2" />
                                                Early Out
                                            </Form.Label>
                                            <p>{employee.custom_schedule.early_out_margin || "N/A"}</p>
                                            </Form.Group>
                                    </Col>
                                      <Col md="4">
                                            <Form.Group>
                                            <Form.Label className="fw-semibold text-primary-light text-sm mb-8">
                                                <Icon fontSize={22} icon="mdi:clock-outline" className="me-2" />
                                                Early Out
                                            </Form.Label>
                                            <p>{employee.custom_schedule.overtime_margin || "N/A"}</p>
                                            </Form.Group>
                                    </Col>
                                       <Col md="4">
                                            <Form.Group>
                                            <Form.Label className="fw-semibold text-primary-light text-sm mb-8">
                                                <Icon fontSize={22} icon="mdi:clock-outline" className="me-2" />
                                                No Of Breaks
                                            </Form.Label>
                                            <p>{employee.custom_schedule.no_of_breaks || "N/A"}</p>
                                            </Form.Group>
                                      </Col>
                                      <Col md="12">
                                        <Row>
                                        <Col md="6">
                                            <Form.Group>
                                            <Form.Label className="fw-semibold text-primary-light text-sm mb-8">
                                                <Icon fontSize={22} icon="mdi:clock-outline" className="me-2" />
                                               Breaks Mints
                                            </Form.Label>
                                            <table className="table table-striped">
                                                <thead>
                                                    <th>Sr No</th>
                                                    <th>Mints</th>
                                                </thead>
                                            <tbody>
                                            {employee.custom_schedule.breaks_mints.split(',').map((e,index)=><tr >
                                                <td>{index+1}</td>
                                                <td>{e} mints</td></tr>) || "N/A"}

                                            </tbody>
                                            </table>
                                            
                                            </Form.Group>
                                      </Col>
                                        </Row>
                                      </Col>
                                </Row> : null 
                            
                        }
                      </>
                    ) : scheduleMode === null ? (
                      <div className="d-flex gap-3 mb-20">
                        <Button
                          variant="outline-primary"
                          className="text-md px-24 py-8 radius-8"
                          onClick={() => setScheduleMode("existing")}
                        >
                          <Icon fontSize={22} icon="mdi:view-list" className="me-2" />
                          Select Existing Schedule
                        </Button>
                        <Button
                          variant="outline-primary"
                          className="text-md px-24 py-8 radius-8"
                          onClick={() => setScheduleMode("new")}
                        >
                          <Icon fontSize={22} icon="mdi:plus" className="me-2" />
                          Create New Schedule
                        </Button>
                      </div>
                    ) : scheduleMode === "existing" ? (
                      <Form.Group className="mb-20">
                        <Form.Label className="fw-semibold text-primary-light text-sm mb-8">
                          <Icon fontSize={22} icon="mdi:calendar-clock" className="me-2" />
                          Select Schedule
                        </Form.Label>
                        <Select
                          options={schedules}
                          value={formData.schedule.schedule_id}
                          onChange={(selected) =>
                            handleSelectChange("schedule", "schedule_id", selected)
                          }
                          placeholder="Select Schedule"
                        />
                      </Form.Group>
                    ) : (
                      <>
                       
                        <Form.Group className="mb-20">
                          <Form.Label className="fw-semibold text-primary-light text-sm mb-8">
                            <Icon fontSize={22} icon="mdi:calendar-week" className="me-2" />
                            Working Days
                          </Form.Label>
                          <div className="d-flex flex-wrap gap-3">
                            {Object.keys(newScheduleData.working_days).map((day) => (
                              <Form.Check
                                key={day}
                                type="checkbox"
                                label={day}
                                checked={newScheduleData.working_days[day]}
                                onChange={() => handleWorkingDayChange(day)}
                                className="check-custom"
                              />
                            ))}
                          </div>
                        </Form.Group>
                        <Row>
                          <Col md={6}>
                            <Form.Group className="mb-20">
                              <Form.Label className="fw-semibold text-primary-light text-sm mb-8">
                                <Icon fontSize={22} icon="mdi:clock-start" className="me-2" />
                                Shift Start
                              </Form.Label>
                              <Form.Control
                                type="time"
                                value={newScheduleData.shift_start}
                                onChange={(e) => handleNewScheduleChange("shift_start", e.target.value)}
                                className="radius-8"
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group className="mb-20">
                              <Form.Label className="fw-semibold text-primary-light text-sm mb-8">
                                <Icon fontSize={22} icon="mdi:clock-end" className="me-2" />
                                Shift End
                              </Form.Label>
                              <Form.Control
                                type="time"
                                value={newScheduleData.shift_end}
                                onChange={(e) => handleNewScheduleChange("shift_end", e.target.value)}
                                className="radius-8"
                              />
                            </Form.Group>
                          </Col>
                          <Col md={4}>
                            <Form.Group className="mb-20">
                              <Form.Label className="fw-semibold text-primary-light text-sm mb-8">
                                <Icon fontSize={22} icon="mdi:clock-in" className="me-2" />
                                Early In (minutes)
                              </Form.Label>
                              <Form.Control
                                type="number"
                                min="0"
                                value={newScheduleData.early_in}
                                onChange={(e) => handleNewScheduleChange("early_in", e.target.value)}
                                className="radius-8"
                                placeholder="e.g., 15"
                              />
                            </Form.Group>
                          </Col>
                          <Col md={4}>
                            <Form.Group className="mb-20">
                              <Form.Label className="fw-semibold text-primary-light text-sm mb-8">
                                <Icon fontSize={22} icon="mdi:clock-fast" className="me-2" />
                                Overtime (hours)
                              </Form.Label>
                              <Form.Control
                                type="number"
                                min="0"
                                value={newScheduleData.overtime}
                                onChange={(e) => handleNewScheduleChange("overtime", e.target.value)}
                                className="radius-8"
                                placeholder="e.g., 2"
                              />
                            </Form.Group>
                          </Col>
                          <Col md={4}>
                            <Form.Group className="mb-20">
                              <Form.Label className="fw-semibold text-primary-light text-sm mb-8">
                                <Icon fontSize={22} icon="mdi:clock-out" className="me-2" />
                                Early Out (minutes)
                              </Form.Label>
                              <Form.Control
                                type="number"
                                min="0"
                                value={newScheduleData.early_out}
                                onChange={(e) => handleNewScheduleChange("early_out", e.target.value)}
                                className="radius-8"
                                placeholder="e.g., 15"
                              />
                            </Form.Group>
                          </Col>
                        </Row>
                        <Form.Group className="mb-20">
                          <Form.Label className="fw-semibold text-primary-light text-sm mb-8">
                            <Icon fontSize={22} icon="mdi:coffee" className="me-2" />
                            Breaks
                          </Form.Label>
                          {newScheduleData.breaks.map((breakItem, index) => (
                            <div key={index} className="d-flex align-items-center mb-8">
                              <Form.Control
                                type="number"
                                min="0"
                                value={breakItem.minutes}
                                onChange={(e) => handleBreakChange(index, e.target.value)}
                                className="radius-8 me-8"
                                placeholder="Minutes"
                                style={{ width: "100px" }}
                              />
                              <Button
                                variant="danger"
                                className="text-md px-8 py-8 radius-8"
                                onClick={() => removeBreak(index)}
                                disabled={newScheduleData.breaks.length === 1}
                              >
                                <Icon fontSize={22} icon="mdi:delete" />
                              </Button>
                            </div>
                          ))}
                          <Button
                            variant="outline-primary"
                            className="text-md px-24 py-8 radius-8 mt-8"
                            onClick={addBreak}
                          >
                            <Icon fontSize={22} icon="mdi:plus" className="me-2" />
                            Add Break
                          </Button>
                        </Form.Group>
                      </>
                    )}
                    <div className="d-flex justify-content-end gap-3">
                      {editMode.schedule && (
                        <Button
                          variant="secondary"
                          className="text-md px-24 py-8 radius-8"
                          onClick={() => {
                            setEditMode({ ...editMode, schedule: false });
                            setScheduleMode(null);
                          }}
                        >
                          <Icon fontSize={22} icon="mdi:close" className="me-2" />
                          Cancel
                        </Button>
                      )}
                      <Button
                        variant={editMode.schedule ? "primary" : "outline-primary"}
                        className="text-md px-24 py-8 radius-8"
                        onClick={() =>
                          editMode.schedule ? handleSave("schedule") : toggleEdit("schedule")
                        }
                        disabled={editMode.schedule && scheduleMode === null}
                      >
                        <Icon
                          fontSize={22}
                          icon={editMode.schedule ? "mdi:content-save" : "mdi:pencil"}
                          className="me-2"
                        />
                        {editMode.schedule ? "Save" : "Edit"}
                      </Button>
                    </div>
                  </Form>
                </Card.Body>
              </Card>
            </Tab>

            {/* Payroll Tab */}
            <Tab eventKey="payroll" title="Payroll">
  <Card className="border">
    <Card.Body>
      <Form>
        <Row>
          <Col md={6}>
            <Form.Group className="mb-20">
              <Form.Label className="fw-semibold text-primary-light text-sm mb-8">
                <Icon fontSize={22} icon="mdi:calendar-month" className="me-2" />
                Payroll Type
              </Form.Label>
              {editMode.payroll ? (
                <Select
                  options={payrollTypeOptions}
                  value={formData.payroll.payroll_type}
                  onChange={(selected) => handleSelectChange("payroll", "payroll_type", selected)}
                  placeholder="Select Payroll Type"
                />
              ) : (
                <p>{employee.salary?.pay_frequency || "N/A"}</p>
              )}
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-20">
              <Form.Label className="fw-semibold text-primary-light text-sm mb-8">
                <Icon fontSize={22} icon="mdi:currency-usd" className="me-2" />
                Currency
              </Form.Label>
              {editMode.payroll ? (
                <Select
                  options={currencyOptions}
                  value={formData.payroll.currency}
                  onChange={(selected) => handleSelectChange("payroll", "currency", selected)}
                  placeholder="Select Currency"
                />
              ) : (
                <p>{employee.salary?.currency_type || "N/A"}</p>
              )}
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-20">
              <Form.Label className="fw-semibold text-primary-light text-sm mb-8">
                <Icon fontSize={22} icon="mdi:cash" className="me-2" />
                Amount
              </Form.Label>
              {editMode.payroll ? (
                <Form.Control
                  type="number"
                  value={formData.payroll.amount}
                  onChange={(e) => handleChange("payroll", "amount", e.target.value)}
                  className="radius-8"
                />
              ) : (
                <p>{employee.salary?.salary_amount || "N/A"}</p>
              )}
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-20">
              <Form.Label className="fw-semibold text-primary-light text-sm mb-8">
                <Icon fontSize={22} icon="mdi:clock-fast" className="me-2" />
                Overtime Rate
              </Form.Label>
              {editMode.payroll ? (
                <Form.Control
                  type="number"
                  value={formData.payroll.overtime_rate}
                  onChange={(e) => handleChange("payroll", "overtime_rate", e.target.value)}
                  className="radius-8"
                />
              ) : (
                <p>{employee.salary?.overtime_rate || "N/A"}</p>
              )}
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-20">
              <Form.Label className="fw-semibold text-primary-light text-sm mb-8">
                <Icon fontSize={22} icon="mdi:star" className="me-2" />
                Pay Grade
              </Form.Label>
              {editMode.payroll ? (
                <Form.Control
                  type="text"
                  value={formData.payroll.pay_grade}
                  onChange={(e) => handleChange("payroll", "pay_grade", e.target.value)}
                  className="radius-8"
                />
              ) : (
                <p>{employee.salary?.pay_grade || "N/A"}</p>
              )}
            </Form.Group>
          </Col>
        </Row>
        <div className="d-flex justify-content-end">
          <Button
            variant={editMode.payroll ? "primary" : "outline-primary"}
            className="text-md px-24 py-8 radius-8"
            onClick={() => (editMode.payroll ? handleSave("payroll") : toggleEdit("payroll"))}
          >
            <Icon
              fontSize={22}
              icon={editMode.payroll ? "mdi:content-save" : "mdi:pencil"}
              className="me-2"
            />
            {editMode.payroll ? "Save" : "Edit"}
          </Button>
        </div>
      </Form>
      <Row className="mt-24">
        <Col md={6}>
          <h6>
            <Icon fontSize={22} icon="mdi:calendar-remove" className="me-2" />
            Assign Quote For Leaves
          </h6>
        </Col>
        <Col md={6} className="d-flex justify-content-end">
          <Button
            variant="primary"
            className="text-md px-24 py-8 radius-8"
            onClick={() => setShowLeaveQuotaModal(true)}
          >
            <Icon fontSize={22} icon="mdi:plus" className="me-2" />
            Assign Quota
          </Button>
        </Col>
      </Row>
      {/* Optional: Display assigned quotas */}
     <Row>
        {
            leaveQuotas.map((e)=>(<>
            <p>{e?.leave_type?.label}</p>
            <p>{e?.days}</p>
            </>))
        }
        <Col md="12">
           
        </Col>
     </Row>
    </Card.Body>
  </Card>

  {/* Leave Quota Modal */}
  <Modal style={{ zIndex: 9999 }} show={showLeaveQuotaModal} onHide={() => setShowLeaveQuotaModal(false)}>
    <Modal.Header closeButton>
      <Modal.Title>
        <h6>
          <Icon fontSize={22} icon="mdi:calendar-remove" className="me-2" />
          Assign Leave Quotas
        </h6>
      </Modal.Title>
    </Modal.Header>
    <Modal.Body>
      <Form>
        {leaveQuotas.map((quota, index) => (
          <Row key={index} className="mb-20 align-items-end">
            <Col md={5}>
              <Form.Group>
                <Form.Label className="fw-semibold text-primary-light text-sm mb-8">
                  <Icon fontSize={22} icon="mdi:calendar-blank" className="me-2" />
                  Leave Type
                </Form.Label>
                <Select
                  options={leaveTypeOptions}
                  value={quota.leave_type}
                  onChange={(selected) => handleLeaveQuotaChange(index, "leave_type", selected)}
                  placeholder="Leave Type"
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label className="fw-semibold text-primary-light text-sm mb-8">
                  <Icon fontSize={22} icon="mdi:numeric" className="me-2" />
                  Number of Days
                </Form.Label>
                <Form.Control
                  type="number"
                  min="1"
                  value={quota.days}
                  onChange={(e) => handleLeaveQuotaChange(index, "days", e.target.value)}
                  className="radius-8"
                  placeholder="e.g., 10"
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Button
                variant="danger"
                className="text-md px-8 py-8 radius-8"
                onClick={() => removeLeaveQuota(index)}
                disabled={leaveQuotas.length === 1}
              >
                <Icon fontSize={22} icon="mdi:delete" />
              </Button>
            </Col>
          </Row>
        ))}
        <Button
          variant="outline-primary"
          className="text-md px-24 py-8 radius-8 mt-8"
          onClick={addLeaveQuota}
        >
          <Icon fontSize={22} icon="mdi:plus" className="me-2" />
          Add Another Quota
        </Button>
      </Form>
    </Modal.Body>
    <Modal.Footer>
      <Button
        variant="secondary"
        className="text-md px-24 py-8 radius-8"
        onClick={() => {
          setShowLeaveQuotaModal(false);
          setLeaveQuotas([{ leave_type: null, days: "" }]);
        }}
      >
        <Icon fontSize={22} icon="mdi:close" className="me-2" />
        Cancel
      </Button>
      <Button
        variant="primary"
        className="text-md px-24 py-8 radius-8"
        onClick={handleSaveLeaveQuotas}
      >
        <Icon fontSize={22} icon="mdi:content-save" className="me-2" />
        Submit
      </Button>
    </Modal.Footer>
  </Modal>
</Tab>

            {/* Password Tab */}
            <Tab eventKey="password" title="Password">
              <Card className="border">
                <Card.Body>
                  <Form>
                    <h6 className="mb-20">
                      <Icon fontSize={22} icon="mdi:lock" className="me-2" />
                      Change Password
                    </h6>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-20">
                          <Form.Label className="fw-semibold text-primary-light text-sm mb-8">
                            <Icon fontSize={22} icon="mdi:lock-outline" className="me-2" />
                            Current Password
                          </Form.Label>
                          <Form.Control
                            type="password"
                            value={formData.password.current_password}
                            onChange={(e) =>
                              handleChange("password", "current_password", e.target.value)
                            }
                            className="radius-8"
                            disabled={!editMode.password}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-20">
                          <Form.Label className="fw-semibold text-primary-light text-sm mb-8">
                            <Icon fontSize={22} icon="mdi:lock-plus" className="me-2" />
                            New Password
                          </Form.Label>
                          <Form.Control
                            type="password"
                            value={formData.password.new_password}
                            onChange={(e) => handleChange("password", "new_password", e.target.value)}
                            className="radius-8"
                            disabled={!editMode.password}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <div className="d-flex justify-content-end mb-24">
                      <Button
                        variant={editMode.password ? "primary" : "outline-primary"}
                        className="text-md px-24 py-8 radius-8"
                        onClick={() =>
                          editMode.password
                            ? handleSave("password", "password")
                            : toggleEdit("password")
                        }
                      >
                        <Icon
                          fontSize={22}
                          icon={editMode.password ? "mdi:content-save" : "mdi:pencil"}
                          className="me-2"
                        />
                        {editMode.password ? "Save Password" : "Change Password"}
                      </Button>
                    </div>

                    <h6 className="mb-20">
                      <Icon fontSize={22} icon="mdi:key" className="me-2" />
                      Change PIN
                    </h6>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-20">
                          <Form.Label className="fw-semibold text-primary-light text-sm mb-8">
                            <Icon fontSize={22} icon="mdi:key-outline" className="me-2" />
                            Current PIN
                          </Form.Label>
                          <Form.Control
                            type="text"
                            value={formData.password.current_pin}
                            onChange={(e) => handleChange("password", "current_pin", e.target.value)}
                            className="radius-8"
                            disabled={!editMode.password}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-20">
                          <Form.Label className="fw-semibold text-primary-light text-sm mb-8">
                            <Icon fontSize={22} icon="mdi:key-plus" className="me-2" />
                            New PIN
                          </Form.Label>
                          <Form.Control
                            type="text"
                            value={formData.password.new_pin}
                            onChange={(e) => handleChange("password", "new_pin", e.target.value)}
                            className="radius-8"
                            disabled={!editMode.password}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <div className="d-flex justify-content-end">
                      <Button
                        variant={editMode.password ? "primary" : "outline-primary"}
                        className="text-md px-24 py-8 radius-8"
                        onClick={() =>
                          editMode.password ? handleSave("password", "pin") : toggleEdit("password")
                        }
                      >
                        <Icon
                          fontSize={22}
                          icon={editMode.password ? "mdi:content-save" : "mdi:pencil"}
                          className="me-2"
                        />
                        {editMode.password ? "Save PIN" : "Change PIN"}
                      </Button>
                    </div>
                  </Form>
                </Card.Body>
              </Card>
            </Tab>

            {/* Separation Tab */}
            <Tab eventKey="separation" title="Separation">
              <Card className="border">
                <Card.Body>
                  <Form>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-20">
                          <Form.Label className="fw-semibold text-primary-light text-sm mb-8">
                            <Icon fontSize={22} icon="mdi:exit-to-app" className="me-2" />
                            Separation Type
                          </Form.Label>
                          {editMode.separation ? (
                            <Select
                              options={separationTypes}
                              value={formData.separation.type}
                              onChange={(selected) => handleSelectChange("separation", "type", selected)}
                              placeholder="Select Separation Type"
                            />
                          ) : (
                            <p>{employee.job?.terminate_status || "N/A"}</p>
                          )}
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-20">
                          <Form.Label className="fw-semibold text-primary-light text-sm mb-8">
                            <Icon fontSize={22} icon="mdi:comment" className="me-2" />
                            Reason
                          </Form.Label>
                          {editMode.separation ? (
                            <Select
                              options={separationReasons}
                              value={formData.separation.reason}
                              onChange={(selected) =>
                                handleSelectChange("separation", "reason", selected)
                              }
                              placeholder="Select Reason"
                            />
                          ) : (
                            <p>{employee.job?.terminate_message || "N/A"}</p>
                          )}
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-20">
                          <Form.Label className="fw-semibold text-primary-light text-sm mb-8">
                            <Icon fontSize={22} icon="mdi:comment-text" className="me-2" />
                            Reason Comments
                          </Form.Label>
                          {editMode.separation ? (
                            <Form.Control
                              as="textarea"
                              value={formData.separation.reason_comments}
                              onChange={(e) =>
                                handleChange("separation", "reason_comments", e.target.value)
                              }
                              className="radius-8"
                              rows={3}
                            />
                          ) : (
                            <p>{employee.job?.reason_comments || "N/A"}</p>
                          )}
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-20">
                          <Form.Label className="fw-semibold text-primary-light text-sm mb-8">
                            <Icon fontSize={22} icon="mdi:calendar" className="me-2" />
                            Separation Date
                          </Form.Label>
                          {editMode.separation ? (
                            <Form.Control
                              type="date"
                              value={formData.separation.date}
                              onChange={(e) => handleChange("separation", "date", e.target.value)}
                              className="radius-8"
                            />
                          ) : (
                            <p>
                              {employee.job?.terminate_date
                                ? new Date(employee.job.terminate_date).toLocaleDateString()
                                : "N/A"}
                            </p>
                          )}
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-20">
                          <Form.Label className="fw-semibold text-primary-light text-sm mb-8">
                            <Icon fontSize={22} icon="mdi:comment-multiple" className="me-2" />
                            Comments
                          </Form.Label>
                          {editMode.separation ? (
                            <Form.Control
                              as="textarea"
                              value={formData.separation.comments}
                              onChange={(e) => handleChange("separation", "comments", e.target.value)}
                              className="radius-8"
                              rows={3}
                            />
                          ) : (
                            <p>{employee.job?.comments || "N/A"}</p>
                          )}
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-20">
                          <Form.Label className="fw-semibold text-primary-light text-sm mb-8">
                            <Icon fontSize={22} icon="mdi:calendar-check" className="me-2" />
                            Clearance Date
                          </Form.Label>
                          {editMode.separation ? (
                            <Form.Control
                              type="date"
                              value={formData.separation.clearance_date}
                              onChange={(e) =>
                                handleChange("separation", "clearance_date", e.target.value)
                              }
                              className="radius-8"
                            />
                          ) : (
                            <p>
                              {employee.job?.clearance_date
                                ? new Date(employee.job.clearance_date).toLocaleDateString()
                                : "N/A"}
                            </p>
                          )}
                        </Form.Group>
                      </Col>
                    </Row>
                    <div className="d-flex justify-content-end">
                      <Button
                        variant={editMode.separation ? "primary" : "outline-primary"}
                        className="text-md px-24 py-8 radius-8"
                        onClick={() =>
                          editMode.separation ? handleSave("separation") : toggleEdit("separation")
                        }
                      >
                        <Icon
                          fontSize={22}
                          icon={editMode.separation ? "mdi:content-save" : "mdi:pencil"}
                          className="me-2"
                        />
                        {editMode.separation ? "Save" : "Edit"}
                      </Button>
                    </div>
                  </Form>
                </Card.Body>
              </Card>
            </Tab>

            {/* Privileges Tab */}
            <Tab eventKey="privileges" title="Privileges">
              <Card className="border">
                <Card.Body>
                  <Form>
                    <Form.Group className="mb-20 check-custom">
                      <Form.Check
                        type="checkbox"
                        label={
                          <>
                            
                            Add Employee
                          </>
                        }
                        checked={formData.privileges.add_employee}
                        onChange={() => handleCheckboxChange("privileges", "add_employee")}
                        disabled={!editMode.privileges}
                      />
                    </Form.Group>
                    <Form.Group className="mb-20 check-custom">
                      <Form.Check
                        type="checkbox"
                        label={
                          <>
                            
                            Mark Attendance
                          </>
                        }
                        checked={formData.privileges.mark_attendance}
                        onChange={() => handleCheckboxChange("privileges", "mark_attendance")}
                        disabled={!editMode.privileges}
                      />
                    </Form.Group>
                    <Form.Group className="mb-20 check-custom">
                      <Form.Check
                        type="checkbox"
                        label={
                          <>
                            
                            Enable GPS
                          </>
                        }
                        checked={formData.privileges.enable_gps}
                        onChange={() => handleCheckboxChange("privileges", "enable_gps")}
                        disabled={!editMode.privileges}
                      />
                    </Form.Group>
                    <div className="d-flex justify-content-end">
                      <Button
                        variant={editMode.privileges ? "primary" : "outline-primary"}
                        className="text-md px-24 py-8 radius-8"
                        onClick={() =>
                          editMode.privileges ? handleSave("privileges") : toggleEdit("privileges")
                        }
                      >
                        <Icon
                          fontSize={22}
                          icon={editMode.privileges ? "mdi:content-save" : "mdi:pencil"}
                          className="me-2"
                        />
                        {editMode.privileges ? "Save" : "Edit"}
                      </Button>
                    </div>
                  </Form>
                </Card.Body>
              </Card>
            </Tab>
          </Tabs>
        </div>
      </div>

      {/* Document Upload Modal */}
      <Modal style={{ zIndex: 9999 }} show={showDocumentModal} onHide={() => setShowDocumentModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            <h6>
              <Icon fontSize={28} icon="mdi:file-upload" className="me-2" />
              Upload Document
            </h6>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-20">
              <Form.Label className="fw-semibold text-primary-light text-sm mb-8">
                <Icon fontSize={22} icon="mdi:file-document" className="me-2" />
                Document Type
              </Form.Label>
              <Select
                options={documentTypeOptions}
                value={documentData.type}
                onChange={(selected) => handleDocumentChange("type", selected)}
                placeholder="Select Document Type"
              />
            </Form.Group>
            <Form.Group className="mb-20">
              <Form.Label className="fw-semibold text-primary-light text-sm mb-8">
                <Icon fontSize={22} icon="mdi:upload" className="me-2" />
                File
              </Form.Label>
              <Form.Control
                type="file"
                onChange={(e) => handleDocumentChange("file", e.target.files[0])}
                className="radius-8"
              />
            </Form.Group>
            <Form.Group className="mb-20">
              <Form.Label className="fw-semibold text-primary-light text-sm mb-8">
                <Icon fontSize={22} icon="mdi:comment" className="me-2" />
                Comments
              </Form.Label>
              <Form.Control
                as="textarea"
                value={documentData.comments}
                onChange={(e) => handleDocumentChange("comments", e.target.value)}
                className="radius-8"
                rows={3}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            className="text-md px-24 py-8 radius-8"
            onClick={() => setShowDocumentModal(false)}
          >
            <Icon fontSize={22} icon="mdi:close" className="me-2" />
            Cancel
          </Button>
          <Button
            variant="primary"
            className="text-md px-24 py-8 radius-8"
            onClick={handleSaveDocument}
          >
            <Icon fontSize={22} icon="mdi:content-save" className="me-2" />
            Save
          </Button>
        </Modal.Footer>
      </Modal>
    </MasterLayout>
  );
};

export default EmployeeDetailPage;